export type DecodedStripeJWTType = {
  accountId: string;
  userId: string;
  domain: string;
  platform: string;
  countries: string[];
  successUrl: string;
  cancelUrl: string;
  recurrenceInterval: "monthly" | "yearly";
  iat: number;
  exp: number;
};

export const OnboardingSteps = {
  CHOOSE_PLAN: "CHOOSE_PLAN",
  CHECKOUT_STARTED: "CHECKOUT_STARTED",
  SUBSCRIBED: "SUBSCRIBED",
  SETUP_STORE: "SETUP_STORE",
  COMPLETED: "COMPLETED",
} as const;

export type OnboardingStepType = (typeof OnboardingSteps)[keyof typeof OnboardingSteps];

export const planNames = {
  STARTER: "STARTER",
  PRO: "PRO",
  SCALE: "SCALE",
  ULTRA: "ULTRA",
  APEX: "APEX",
  TITAN: "TITAN",
  C4_5M: "C4_5M",
} as const;

export type PlanNamesType = keyof typeof planNames;

export const userStatus = {
  INVITED: "INVITED",
  DISABLED: "DISABLED",
  USER_SETUP_COMPLETED: "USER_SETUP_COMPLETED",
} as const;

export type UserStatusNamesType = keyof typeof userStatus;

export const PLAN_LIMITS = {
  [planNames.STARTER]: 75000,
  [planNames.PRO]: 200000,
  [planNames.SCALE]: 400000,
  [planNames.ULTRA]: 800000,
  [planNames.APEX]: 1200000,
  [planNames.TITAN]: 1600000,
  [planNames.C4_5M]: 4500000,
} as const;

// Frontend-facing plan labels. Only plans whose identifier differs from the name we
// want to show need an entry; everything else falls back to its identifier.
export const PLAN_DISPLAY_NAMES: Partial<Record<PlanNamesType, string>> = {
  [planNames.C4_5M]: "4.5M",
};

export const getPlanDisplayName = (plan: PlanNamesType | string): string =>
  PLAN_DISPLAY_NAMES[plan as PlanNamesType] ?? plan;

// Top-tier plans bill overage beyond their limit instead of blocking.
// C4_5M is a custom plan above TITAN: it is deliberately absent from `plans`
// (so it is never offered in plan selection or upgrades) but still gets overage billing.
export const OVERAGE_PLANS = [planNames.TITAN, planNames.C4_5M] as const;

export const isOveragePlan = (plan?: string | null): boolean =>
  OVERAGE_PLANS.includes(plan as (typeof OVERAGE_PLANS)[number]);

export const OVERAGE_BLOCK_SIZE = 100_000; // sessions per billed block
export const OVERAGE_PRICE_CENTS = 30_000; // $300 per block

// Entity Types
export const INVOICE = "INVOICE";
export const CUSTOMER = "CUSTOMER";
export const ESTIMATE = "ESTIMATE";
export const PRODUCT = "PRODUCT";

export const USER_REGISTERED = "USER_REGISTERED";

export type EntityType = "INVOICE" | "CUSTOMER" | "ESTIMATE" | "PRODUCT";

export const qboEntityType: { [key: string]: EntityType } = {
  INVOICE,
  CUSTOMER,
  ESTIMATE,
};

export type MappingType = {
  [key: string]: {
    id: string;
    name: string;
  };
};

export const mondayWebhookEvents = {
  item_deleted: "item_deleted",
  change_column_value: "change_column_value",
  change_status_column_value: "change_status_column_value",
  change_subitem_column_value: "change_subitem_column_value",
  change_specific_column_value: "change_specific_column_value",
  change_name: "change_name",
  create_item: "create_item",
  item_archived: "item_archived",
  item_moved_to_any_group: "item_moved_to_any_group",
  item_moved_to_specific_group: "item_moved_to_specific_group",
  item_restored: "item_restored",
  create_subitem: "create_subitem",
  change_subitem_name: "change_subitem_name",
  move_subitem: "move_subitem",
  subitem_archived: "subitem_archived",
  subitem_deleted: "subitem_deleted",
  create_column: "create_column",
  create_update: "create_update",
  edit_update: "edit_update",
  delete_update: "delete_update",
  create_subitem_update: "create_subitem_update",
};

export const QBO_ESTIMATE_URL = "https://app.qbo.intuit.com/app/estimate?txnId=";
export const QBO_INVOICE_URL = "https://app.qbo.intuit.com/app/invoice?txnId=";
export const QBO_CUSTOMER_URL = "https://app.qbo.intuit.com/app/customerdetail?nameId=";

export type OurRoles = "ADMIN" | "STANDARD";

export const roles: Record<OurRoles, OurRoles> = {
  ADMIN: "ADMIN",
  STANDARD: "STANDARD",
};

export type OurStages = "FIRST_TIME_SETUP" | "COMPLETE";

export const initStages: Record<OurStages, OurStages> = {
  // SUBSCRIBE: "SUBSCRIBE",
  // CONNECTIONS: "CONNECTIONS",
  // DATA_MAPPING: "DATA_MAPPING",
  FIRST_TIME_SETUP: "FIRST_TIME_SETUP",
  COMPLETE: "COMPLETE",
};

export type InitResponseType = {
  stage: OurStages;
  role: OurRoles;
};
